import { waitUntilGliderIsLoaded } from "../_waitUntilGliderIsLoaded";

/**
 * Handles the initialization and rendering of a Glider.js slider
 * for product teaser cards (`.sbt-card`).
 *
 * @class NProductTeaasersSlider
 */
class NProductTeaasersSlider {
    static sliderBreakpoints = {
        default: [
            {
                breakpoint: 540,
                settings: { slidesToShow: 2 },
            },
            {
                breakpoint: 960,
                settings: { slidesToShow: 3 },
            },
        ],
        offset: [
            {
                breakpoint: 540,
                settings: { slidesToShow: 1.5 },
            },
            {
                breakpoint: 960,
                settings: { slidesToShow: 2.5 },
            },
            {
                breakpoint: 1440,
                settings: { slidesToShow: 3.5 },
            },
        ],
    }
    /**
     * @param {HTMLElement} wrapperEl - The container element that holds the teaser cards.
     */
    constructor(wrapperEl) {
        /** @type {HTMLElement} */
        this.wrapperEl = wrapperEl

        /** @type {NodeListOf<HTMLElement>} */
        this.teasers = this.wrapperEl.querySelectorAll('.sbt-card');

        // If there are no teaser cards, we can skip initialization
        if (!this.teasers.length) return;

        this._isOffsetSlider = !!this.wrapperEl.closest('.section--image-card-collection-as-offset-slider');

        // Create and append the slider markup to the wrapper
        this._slider = this._createSlider();
        this.wrapperEl.appendChild(this._slider);

        // Initialize the Glider slider instance
        this._initSlider();
    }

    /**
     * Creates the required DOM structure for the Glider slider:
     * - Wrapper
     * - Frame
     * - Controls (arrows + bullets)
     *
     * @private
     * @returns {HTMLElement} The root slider element.
     */
    _createSlider() {
        // Create outer wrapper (for styling/layout purposes)
        const sliderWrapper = document.createElement("div");
        sliderWrapper.classList.add("slider-wrapper");

        // Create the main slider element (custom element)
        this._slider = document.createElement('toujou-slider');
        this._slider.classList.add("n-product-teasers-slider", 'slider', 'slider--primary');

        // Create the frame element where the slides (cards) will live
        this._sliderFrame = document.createElement("div");
        this._sliderFrame.classList.add('slider__frame');
        this._sliderFrame.setAttribute('tabindex', "0");

        // Move teaser cards into the slider frame
        this.teasers.forEach((teaser) => {
            this._sliderFrame.appendChild(teaser);
        });

        this._slider.appendChild(this._sliderFrame);

        // Wrapper for navigation arrows and pagination bullets
        this._controlsWrapper = document.createElement('div');
        this._controlsWrapper.classList.add('slider__controls');

        // Bullet pagination container
        this._sliderBulletsContainer = document.createElement('div');
        this._sliderBulletsContainer.classList = 'slider__pagination';

        // Previous arrow button
        this._sliderArrowPrev = document.createElement('button');
        this._sliderArrowPrev.classList = 'slider__control slider__control--prev';
        this._sliderArrowPrev.setAttribute('aria-label', 'Zum vorherigen Slide');
        this._sliderArrowPrev.setAttribute('tabindex', '0');

        // Next arrow button
        this._sliderArrowNext = document.createElement('button');
        this._sliderArrowNext.classList = 'slider__control slider__control--next';
        this._sliderArrowNext.setAttribute('aria-label', 'Zum nächsten Slide');
        this._sliderArrowNext.setAttribute('tabindex', '0');

        // Append controls to wrapper
        this._controlsWrapper.appendChild(this._sliderArrowPrev);
        this._controlsWrapper.appendChild(this._sliderBulletsContainer);
        this._controlsWrapper.appendChild(this._sliderArrowNext);

        // Append wrapper to slider
        this._slider.appendChild(this._controlsWrapper);

        return this._slider;
    }

    /**
     * Initializes the Glider.js slider with custom configuration
     * and binds the controls (arrows & dots).
     *
     * @private
     */
    _initSlider() {
        const correctSlidesToShow = this._isOffsetSlider ? 1.5 : 1;
        const currentBreakpoints = this._isOffsetSlider
            ? NProductTeaasersSlider.sliderBreakpoints.offset
            : NProductTeaasersSlider.sliderBreakpoints.default;

        try {
            /** @type {Glider.Options} */
            const sliderOptions = {
                slidesToShow: correctSlidesToShow,
                slidesToScroll: 1,
                rewind: true,
                draggable: true,
                scrollLock: true,
                responsive: currentBreakpoints,
                arrows: {
                    prev: this._sliderArrowPrev,
                    next: this._sliderArrowNext,
                },
                dots: this._sliderBulletsContainer,
            };

            // Initialize and store Glider instance
            this._slider._glider = new Glider(this._sliderFrame, sliderOptions);
            this._slider._glider.refresh(true);
        } catch (error) {
            console.log('Could not initialize the product teaser slider', error)
        }
    }
}

/**
 * Initializes all teaser sliders on the page.
 * Waits until Glider.js is loaded before creating instances.
 *
 * @async
 * @returns {Promise<void>}
 */
async function initNProductTeaserSlider() {
    /** @type {NodeListOf<HTMLElement>} */
    const nProductTeaserSliders = document.querySelectorAll('.n-product-teasers-slider');
    if (!nProductTeaserSliders) return;

    // Ensure Glider is fully loaded before initializing sliders
    const gliderAvailable = await waitUntilGliderIsLoaded();

    // Initialize each slider instance
    nProductTeaserSliders.forEach((wrapperEl) => {
        new NProductTeaasersSlider(wrapperEl);
    })
}

// Initialize immediately if DOM is already loaded, otherwise wait for DOMContentLoaded
if (document.readyState !== "loading") {
    initNProductTeaserSlider();
} else {
    document.addEventListener("DOMContentLoaded", initNProductTeaserSlider);
}
